// Library
import React, { useState } from 'react'
import * as yup from 'yup'
import { useHistory } from 'react-router-dom'

// Routes
import { PATH_FORGOT_PASSWORD_SUCCESS, PATH_LOGIN } from 'routes/paths'

// Services
import dialog from 'services/dialog'

// Helper
import { resolveErrorMessage } from 'helpers'

// Use
import useForm from 'use/form'
import { useDesktop } from 'use/media'
import useMobile from 'ui/Datatable/mobile'

// Ui
import Box from 'ui/Box'
import Row from 'ui/Row'
import Col from 'ui/Col'
import Space from 'ui/Space'
import Heading from 'ui/Heading'
import Text from 'ui/Text'
import Input from 'ui/Form/Input'
import Button from 'ui/Button'
import Link from 'ui/Link'
import LoginBanner from 'ui/LoginBanner'

// Components
import PopupSocialLogin from 'components/Register/PopupSocialLogin'

// Icon
import IconArrowLeft from 'ui/Icons/IconArrowLeft'

// Api
import { sendForgotPasswordEmail } from 'api/password'

// Validation schema
const validationSchema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .email('Please input a valid email address')
    .required('Please input email address'),
})

// InitialForm
const initialForm = {
  email: '',
}

export default function PageForgotPassword() {
  const history = useHistory()
  const isDesktop = useDesktop()
  const isMobile = useMobile()
  const [loading, setLoading] = useState(false)

  // UseFormHook
  const { form, errors, validate, onChange, onBlur, setFieldError } = useForm({
    schema: validationSchema,
    initialForm,
  })

  // Event
  const onEnterPress = (e) => {
    if (e.keyCode === 13 && !loading) {
      e.preventDefault()
      onClickSubmit()
    }
  }

  const onClickSubmit = async () => {
    // Validation
    try {
      await validate()
    } catch (error) {
      return
    }

    setLoading(true)

    // Call api
    try {
      await sendForgotPasswordEmail(form.email)
      history.replace(PATH_FORGOT_PASSWORD_SUCCESS)
    } catch (error) {
      if (error.code === 'FORGOTPASSWD.SOCIAL.SIGNINONLY') {
        dialog.create(PopupSocialLogin)
        return
      }
      if (error.code === 'SIGNIN.ACCOUNT.NOT.FOUND') {
        dialog.alert({
          title: 'User not found in the System.',
          message: 'Please contact Admin.',
        })
        return
      }

      setFieldError('email', await resolveErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        height: isDesktop ? '100vh' : 'auto',
        background: '#F4FBFF',
        padding: isMobile ? 0 : 40,
      }}
      className="x-login-page d-flex align-items-center justify-content-center"
    >
      <Box
        shadow={!isMobile}
        style={{ padding: isMobile ? '0' : '60px 10px', width: '100%' }}
      >
        <Row noGutters style={{ height: 'auto' }}>
          <Col
            sm={{ span: 12, vcenter: true }}
            md={{ span: 7, vcenter: true }}
            lg={{ span: 7, vcenter: true }}
          >
            <LoginBanner></LoginBanner>
          </Col>
          <Col
            sm={{ span: 12 }}
            md={{ span: 5 }}
            lg={{ span: 5 }}
            xl={{ span: 4 }}
            style={{ marginTop: isMobile ? 20 : 40 }}
          >
            <Row noGutters>
              <Col
                xs={{ span: 10, offset: 1 }}
                sm={{ span: 8, offset: 2 }}
                md={{ span: 12, offset: 0 }}
              >
                <Link path={PATH_LOGIN}>
                  <Space
                    direction="horizontal"
                    size={5}
                    style={{ width: '100%' }}
                  >
                    <IconArrowLeft />
                    <Text color="#5badfa" size={isDesktop ? 16 : 15}>
                      Go back
                    </Text>
                  </Space>
                </Link>
                <Heading
                  size={isDesktop ? 30 : 20}
                  paddingBottom={26}
                  paddingTop={15}
                >
                  Forgot your password?
                </Heading>
                <Text
                  color="#000000"
                  size={isDesktop ? 16 : 15}
                  paddingBottom={18}
                >
                  First, let’s find your account by providing your e-mail
                  address
                </Text>
              </Col>
              <Col
                xs={{ span: 10, offset: 1 }}
                sm={{ span: 8, offset: 2 }}
                md={{ span: 11, offset: 0 }}
              >
                <Input
                  name="email"
                  label="E-mail address"
                  placeholder="Enter your e-mail address"
                  value={form.email}
                  errors={errors}
                  onChange={(value, name) => onChange(value, name, false)}
                  onBlur={onBlur}
                  onKeyDown={onEnterPress}
                />
              </Col>

              <Col
                xs={{ span: 10, offset: 1 }}
                sm={{ span: 8, offset: 2 }}
                md={{ span: 11, offset: 0 }}
              >
                <Button
                  width={200}
                  type="submit"
                  block
                  loading={loading}
                  onClick={onClickSubmit}
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Box>
    </div>
  )
}
